import React, { useState, useEffect } from "react";
import logo from "../assets/logo.png";
import { useAuth } from "../utils/authentication";
import { useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";

function LoginPage() {
    const [email, setEmail] = useState("");
    const [passcode, setPasscode] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const { isAuthenticated, login } = useAuth();
    const apiUrl = process.env.REACT_APP_API_URL || "";
    const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const navigate = useNavigate();

    useEffect(() => {
        if (isAuthenticated) {
            navigate("/chat");
        }
    }, [isAuthenticated, navigate]);

    const handleSimpleLogin = async (e) => {
        e.preventDefault();
        setError("");

        const trimmedEmail = email.trim();
        if (!trimmedEmail) {
            setError("Please enter your email address.");
            return;
        }

        if (!passcode) {
            setError("Please enter the passcode.");
            return;
        }

        setLoading(true);
        try {
            const res = await fetch(`${apiUrl}/login`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    email: trimmedEmail,
                    passcode: passcode,
                }),
            });

            const data = await res.json();
            if (res.ok) {
                setError("");
                localStorage.setItem("userId", data.email);
                if (data.token) {
                    localStorage.setItem("authToken", data.token);
                }
                login();
            } else {
                setError(data.error || "Login failed. Please check your credentials.");
            }
        } catch (err) {
            setError("Unable to connect to server. Please check your backend.");
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleLogin = async (response) => {
        const { credential } = response;
        setLoading(true);
        setError("");

        try {
            const res = await fetch(`${apiUrl}/google-login`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ token: credential }),
            });

            const data = await res.json();
            if (res.ok) {
                setError("");
                localStorage.setItem("userId", data.email);
                login();
            } else {
                setError("Google login failed. Please contact admin.");
            }
        } catch (err) {
            setError("Google login failed. Please contact admin.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-black transition-colors duration-300 flex items-center justify-center px-4">
            <div className="w-full max-w-md bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 shadow-xl rounded-2xl p-8 flex flex-col items-center">
                <img
                    className="h-14 mx-auto mb-6 object-contain"
                    src={logo}
                    alt="I-Stem Agent Logo"
                />
                
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white text-center">
                    Welcome to I-Stem Agent
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center mt-1 mb-6">
                    Enter your email and passcode to continue
                </p>

                {error && (
                    <div
                        className="w-full mb-5 p-3 rounded-lg bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm"
                        role="alert"
                    >
                        {error}
                    </div>
                )}

                <form onSubmit={handleSimpleLogin} className="w-full space-y-4">
                    <div>
                        <label
                            htmlFor="email"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                        >
                            Email Address
                        </label>
                        <input
                            id="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@company.com"
                            required
                            disabled={loading}
                            className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-all text-sm disabled:opacity-50"
                        />
                    </div>

                    <div>
                        <label
                            htmlFor="passcode"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                        >
                            Passcode
                        </label>
                        <input
                            id="passcode"
                            type="password"
                            value={passcode}
                            onChange={(e) => setPasscode(e.target.value)}
                            placeholder="Enter access passcode"
                            required
                            disabled={loading}
                            className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-all text-sm disabled:opacity-50"
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-2.5 px-4 mt-2 rounded-lg bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-medium text-sm transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-zinc-900 disabled:opacity-50 cursor-pointer flex items-center justify-center"
                    >
                        {loading ? (
                            <div className="flex items-center space-x-2">
                                <svg
                                    className="animate-spin h-4 w-4 text-white"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    ></circle>
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8v8H4z"
                                    ></path>
                                </svg>
                                <span>Signing in...</span>
                            </div>
                        ) : (
                            "Sign In"
                        )}
                    </button>
                </form>

                {googleClientId && (
                    <div className="w-full mt-6 flex flex-col items-center">
                        <div className="relative w-full mb-4 flex items-center justify-center">
                            <div className="border-t border-gray-200 dark:border-zinc-700 w-full"></div>
                            <span className="bg-white dark:bg-zinc-900 px-3 text-xs text-gray-400 dark:text-zinc-500 absolute">
                                OR
                            </span>
                        </div>
                        <div className="w-full flex justify-center">
                            <GoogleLogin
                                onSuccess={handleGoogleLogin}
                                onError={() =>
                                    setError("Google Login failed. Please try again.")
                                }
                                theme="outline"
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default LoginPage;
